"use client";

import { SelectCountryPhone } from "@/app/(events)/[slug]/purchase/select-phone";
import { Icons } from "@/components/icons";
import { cn } from "@/lib/utils";
import { Eye, EyeOff } from "lucide-react";
import { ReactNode, forwardRef, useState } from "react";

export const inputClass =
  "block h-10 w-full rounded-md border border-transparent bg-theme-input px-3 text-base text-white placeholder:text-theme-gray focus:outline-none focus-visible:border-theme-accent aria-[invalid=true]:border-[#F8285A] disabled:opacity-50 sm:h-[38px] sm:text-xs";

export function AuthField({
  label,
  htmlFor,
  required = true,
  error,
  className,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string | string[];
  className?: string;
  children: ReactNode;
}) {
  const message = Array.isArray(error) ? error[0] : error;
  return (
    <div className={cn("grid gap-2", className)}>
      <label htmlFor={htmlFor} className="text-base font-medium text-[#E3E3E3]">
        {label}
        {required ? (
          <span aria-hidden className="ml-0.5 text-[#F8285A]">
            *
          </span>
        ) : null}
      </label>
      {children}
      {message ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs text-[#F8285A]">
          {message}
        </p>
      ) : null}
    </div>
  );
}

export const AuthInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(({ className, invalid, ...props }, ref) => (
  <input
    ref={ref}
    aria-invalid={invalid || undefined}
    aria-describedby={invalid && props.id ? `${props.id}-error` : undefined}
    className={cn(inputClass, className)}
    {...props}
  />
));
AuthInput.displayName = "AuthInput";

export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & { invalid?: boolean }
>(({ className, ...props }, ref) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <AuthInput
        ref={ref}
        type={visible ? "text" : "password"}
        autoCapitalize="none"
        autoCorrect="off"
        placeholder="********"
        className={cn("pr-10", className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
        aria-pressed={visible}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-md text-theme-gray hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";

export function PhoneInput({
  countryCode,
  onCountryChange,
  id,
  invalid,
  inputProps,
}: {
  countryCode: string;
  onCountryChange: (isoCode: string) => void;
  id: string;
  invalid?: boolean;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
}) {
  return (
    <div className="relative text-white">
      <div className="absolute inset-y-0 left-0 flex items-center">
        <SelectCountryPhone onChange={onCountryChange} value={countryCode} />
      </div>
      <AuthInput
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder="8123456789"
        invalid={invalid}
        className="pl-28"
        {...inputProps}
      />
    </div>
  );
}

export function SubmitButton({ loading, children }: { loading?: boolean; children: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="flex h-12 w-full items-center justify-center rounded-md bg-theme-accent text-lg font-medium text-white transition-colors hover:bg-theme-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-theme-black3 disabled:opacity-60"
    >
      {loading ? <Icons.spinner className="mr-2 h-4 w-4 animate-spin" /> : null}
      {children}
    </button>
  );
}

export function OrDivider() {
  return <div className="text-center text-[13px] text-theme-gray">Atau</div>;
}
